import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    Tabs,
    Tab,
    CircularProgress,
    InputAdornment,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { loginWithEmail, registerWithEmail, resetPassword } from '../firebase/auth';
import { setDocument } from '../firebase/firestore';

import UniversityLogo from './Nims_Logo.png';

/* ================= VALIDATION ================= */

const loginSchema = yup.object({
    email: yup.string().email('Invalid email').required('Email is required'),
    password: yup.string().min(6, 'Minimum 6 characters').required('Password is required'),
});

const registerSchema = yup.object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    phone: yup
        .string()
        .matches(/^[0-9]{10}$/, 'Phone must be exactly 10 digits')
        .required('Phone number is required'),
    companyName: yup.string().required('Company name is required'),
    password: yup
        .string()
        .required('Password is required')
        .min(8, 'Minimum 8 characters')
        .matches(/[A-Z]/, 'At least one uppercase letter')
        .matches(/[a-z]/, 'At least one lowercase letter')
        .matches(/[0-9]/, 'At least one number')
        .matches(/[^A-Za-z0-9]/, 'At least one special character'),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref('password')], 'Passwords must match')
        .required('Confirm password is required'),
});

/* ================= TAB PANEL ================= */

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

/* ================= COMPONENT ================= */

export default function LoginPage() {
    const navigate = useNavigate();

    const [tab, setTab] = useState(0);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [openReset, setOpenReset] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSuccess, setResetSuccess] = useState('');

    const loginForm = useForm({ resolver: yupResolver(loginSchema) });

    const registerForm = useForm({
        resolver: yupResolver(registerSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            companyName: '',
            password: '',
            confirmPassword: '',
        },
    });

    /* ================= HANDLERS ================= */

    const handleLogin = useCallback(async (data) => {
        try {
            setLoading(true);
            setError('');
            await loginWithEmail(data.email, data.password);
            navigate('/');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    const handleRegister = useCallback(async (data) => {
        try {
            setLoading(true);
            setError('');

            const user = await registerWithEmail(data.email, data.password);

            await setDocument('users', user.uid, {
                uid: user.uid,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phone: data.phone,
                companyName: data.companyName,
                role: 'student',
                createdAt: new Date(),
                updatedAt: new Date(),
                emailVerified: false,
                profileComplete: false,
            });

            navigate('/');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    const handleResetPassword = async () => {
        try {
            setError('');
            await resetPassword(resetEmail);
            setResetSuccess('Password reset email sent successfully');
            setOpenReset(false);
        } catch (err) {
            setError(err.message);
        }
    };

    /* ================= UI ================= */

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: '#0f172a',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* -------- PAGE HEADER -------- */}
            <Box
                sx={{
                    width: '100%',
                    borderBottom: '1px solid #1e293b',
                    backgroundColor: '#f8f8f8ff',
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            minHeight: { xs: 56, md: 80 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: {
                                xs: 'flex-start',
                                md: 'center',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <Box
                                component="img"
                                src={UniversityLogo}
                                alt="University Logo"
                                sx={{
                                    height: {
                                        xs: 32,
                                        sm: 40,
                                        md: 60,
                                    },
                                    width: 'auto',
                                }}
                            />

                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: '#061031ff',
                                    fontSize: {
                                        xs: '0.9rem',
                                        sm: '1.1rem',
                                        md: '1.6rem',
                                    },
                                    textAlign: {
                                        xs: 'left',
                                        md: 'center',
                                    },
                                    maxWidth: {
                                        xs: '240px',
                                        sm: '100%',
                                    },
                                    lineHeight: 1.2,
                                    wordBreak: 'break-word',
                                }}
                            >
                                NIMS SUSHMA INNOVATION AND INCUBATION CENTER (NSIIC)
                            </Typography>

                        </Box>
                    </Box>
                </Container>
            </Box>



            {/* -------- LOGIN CONTENT -------- */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Container maxWidth="sm">
                    <Paper sx={{ p: 4, borderRadius: 2 }}>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        {resetSuccess && <Alert severity="success" sx={{ mb: 2 }}>{resetSuccess}</Alert>}

                        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                            <Tab label="Login" />
                            <Tab label="Register" />
                        </Tabs>

                        {/* -------- LOGIN -------- */}
                        <TabPanel value={tab} index={0}>
                            <form onSubmit={loginForm.handleSubmit(handleLogin)}>
                                <Controller
                                    name="email"
                                    control={loginForm.control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Email"
                                            margin="normal"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    name="password"
                                    control={loginForm.control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Password"
                                            type={showPassword ? 'text' : 'password'}
                                            margin="normal"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />

                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setOpenReset(true)}
                                    sx={{ mt: 1, textTransform: 'none' }}
                                >
                                    Forgot password?
                                </Button>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        mt: 3,
                                        py: 1.2,
                                        bgcolor: '#dc2626',
                                        '&:hover': { bgcolor: '#b91c1c' },
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={22} /> : 'Sign In'}
                                </Button>
                            </form>
                        </TabPanel>

                        {/* -------- REGISTER -------- */}
                        <TabPanel value={tab} index={1}>
                            <form onSubmit={registerForm.handleSubmit(handleRegister)}>
                                {['firstName', 'lastName', 'email', 'companyName'].map((name) => (
                                    <Controller
                                        key={name}
                                        name={name}
                                        control={registerForm.control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label={name.replace(/([A-Z])/g, ' $1')}
                                                margin="normal"
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                ))}

                                <Controller
                                    name="phone"
                                    control={registerForm.control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Phone Number"
                                            margin="normal"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            inputProps={{ maxLength: 10 }}
                                            onChange={(e) =>
                                                field.onChange(e.target.value.replace(/\D/g, '').slice(0, 10))
                                            }
                                        />
                                    )}
                                />

                                {['password', 'confirmPassword'].map((name) => (
                                    <Controller
                                        key={name}
                                        name={name}
                                        control={registerForm.control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                {...field}
                                                label={name === 'password' ? 'Password' : 'Confirm Password'}
                                                fullWidth
                                                margin="normal"
                                                size="small"
                                                type={name === 'password' ? (showPassword ? 'text' : 'password') : (showPassword ? 'text' : 'password')}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowPassword(!showPassword)}
                                                                edge="end"
                                                                size="small"
                                                            >
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                ))}


                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        mt: 3,
                                        py: 1.2,
                                        bgcolor: '#dc2626',
                                        '&:hover': { bgcolor: '#b91c1c' },
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={22} /> : 'Create Account'}
                                </Button>
                            </form>
                        </TabPanel>
                    </Paper>
                </Container>
            </Box>

            {/* -------- RESET PASSWORD -------- */}
            <Dialog open={openReset} onClose={() => setOpenReset(false)}>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Registered Email"
                        margin="normal"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={handleResetPassword}
                    >
                        Send Reset Email
                    </Button>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
